import { useFormik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl';
import { Modal } from 'rsuite'
import * as Yup from 'yup';
import useAccessToken from '../../hooks/AccessToken';
import { updateProfilePicture } from '../../api/UsersCRUD';
import Swal from 'sweetalert2';
import { insertAttachment } from '../../api/ResourceAPI';
import useUser from '../../hooks/User';
import * as auth from '../../app/modules/auth/redux/AuthRedux'
import { useDispatch } from 'react-redux';
import { MAX_FILE_SIZE } from '../../constant/General';

interface ModalEditAvatarProps {
    open: boolean
    onClose: () => void
    userProfile: any
    mutateProfile: () => void
}


const ModalEditAvatar = ({ open, onClose, userProfile, mutateProfile }: ModalEditAvatarProps) => {
    const intl = useIntl();
    const user = useUser()
    const dispatch = useDispatch()
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const token = useAccessToken()


    const formik = useFormik({
        initialValues: {
            profilePicture: userProfile || ''
        },
        validationSchema: Yup.object().shape({
            profilePicture: Yup.string().notRequired().nullable()
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true)
            try {
                let countResourceError = 0
                if (values.profilePicture) {
                    try {
                        const attachmentResponse = await insertAttachment(values.profilePicture, 'PROFILE PICTURE', token);
                        if (attachmentResponse.data.success) {
                            values.profilePicture = attachmentResponse.data.data[0];
                        } else {
                            countResourceError++
                        }
                    } catch (error) {
                        countResourceError++
                    }
                }

                if (countResourceError > 0) {
                    Swal.fire({
                        icon: 'error',
                        title: intl.formatMessage({ id: 'ALERT.TITLE.ERROR' }),
                        text: intl.formatMessage({ id: 'ALERT.TITLE.ERROR_RESOURCE' })
                    })
                    return
                } else {
                    const response = await updateProfilePicture({
                        profilePicture: values.profilePicture,
                        token: token,
                    })
                    if (response.data.success) {
                        Swal.fire({
                            icon: 'success',
                            title: intl.formatMessage({ id: 'ALERT.TITLE.SUCCESS' }),
                            text: intl.formatMessage({ id: 'ALERT.TITLE.SUCCESS_UPDATE' })
                        })

                        const newUserData = { ...user, data: { ...user.data, profile_picture: values.profilePicture } };
                        dispatch(auth.actions.fulfillUser(newUserData))
                        onClose()
                    }
                }
            } catch (error: any) {
                const errorMessage = error.response?.data?.message || intl.formatMessage({ id: 'ALERT.TITLE.ERROR_UPDATE' });
                Swal.fire({
                    icon: 'error',
                    title: intl.formatMessage({ id: 'ALERT.TITLE.ERROR' }),
                    text: errorMessage,
                });
            } finally {
                setSubmitting(false)
                mutateProfile()
            }
        }
    })

    const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        if (file) {
            try {
                if (file.size > MAX_FILE_SIZE) {
                    Swal.fire({
                        icon: 'error',
                        title: intl.formatMessage({ id: 'ALERT.TITLE.ERROR' }),
                        text: intl.formatMessage({ id: 'ALERT.TITLE.ERROR_FILE_SIZE' })
                    });
                    return;
                }
                formik.setFieldValue('profilePicture', file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setAvatarPreview(reader.result as string);
                };
                reader.readAsDataURL(file);
            } catch (error) {
                console.error('Error compressing image:', error);
                Swal.fire({
                    icon: 'error',
                    title: intl.formatMessage({ id: 'ALERT.TITLE.ERROR' }),
                    text: 'Error processing image'
                });
            }
        } else {
            formik.setFieldValue('profilePicture', null);
            setAvatarPreview(null);
        }
    };
    useEffect(() => {
        const getRsModalWrapper: HTMLDivElement | null = document.querySelector('.rs-modal-wrapper')
        if (getRsModalWrapper) {
            getRsModalWrapper.style.display = 'flex'
            getRsModalWrapper.style.alignItems = 'center'
            getRsModalWrapper.style.justifyContent = 'center'
        }
    }, [])

    return (
        <Modal open={open} onClose={onClose} size={'sm'}>
            <div className="modal-header">
                <h5>{intl.formatMessage({ id: "FORM.LABEL.EDIT_PRODILE_PICTURE" })}</h5>
            </div>

            <form onSubmit={formik.handleSubmit} noValidate>
                <div className="modal-body">
                    {/* Avatar */}
                    <label className='form-label fs-6 fw-bolder text-dark'>Avatar (Optional)</label>
                    <div className="mb-5 position-relative">
                        {formik.values.profilePicture && formik.values.profilePicture !== '-' ?
                            <img
                                className="mb-3"

                                style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                                src={avatarPreview || formik.values.profilePicture}
                                alt='user-icon'
                            />
                            :
                            <img
                                className="mb-3"

                                style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                                src={avatarPreview || '/media/icons/duotune/communication/com013.svg'}
                                alt='user-icon'
                            />
                        }
                        {formik.values.profilePicture && formik.values.profilePicture !== '-' && (
                            <button
                                type="button"
                                className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                style={{
                                    position: 'absolute',
                                    top: '1px',
                                    left: '-7px',
                                }}
                                onClick={() => {
                                    formik.setFieldValue('profilePicture', null);
                                    setAvatarPreview(null);
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = '';
                                    }
                                }}
                            >
                                <i className="bi bi-x fs-2"></i>
                            </button>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarChange}
                            className="form-control form-control-lg"
                        />
                        {formik.touched.profilePicture && formik.errors.profilePicture && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.profilePicture}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button className='btn btn-secondary me-3'
                        onClick={onClose}>
                        {intl.formatMessage({ id: 'FORM.ACTION.CLOSE' })}
                    </button>
                    <button type="submit" className={`btn btn-primary me-10 ${formik.isSubmitting ? 'disabled' : ''}`} data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}>
                        <span className="indicator-label">
                            {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                        </span>
                        <span className="indicator-progress">
                            Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                    </button>
                </div>
            </form>
        </Modal >
    )
}

export default ModalEditAvatar